import { ErrorModal, getMomentTz, MesfModal } from "@dexteel/mesf-core";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Skeleton,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
	getDayShiftOptions,
	getLineScheduleDayImpact,
	type LineScheduleDayImpact,
	type ShiftSpellChange,
} from "@/shared/repositories/LineScheduleRepository";
import type { DayKind } from "../utils/calendarUtils";
import { shiftsPerDayOf, toDateKey } from "../utils/calendarUtils";
import { ShiftSpellToggles } from "./ShiftSpellToggles";

const moment = getMomentTz();

const OPTIONS: { kind: DayKind; title: string; shifts: string }[] = [
	{ kind: "8h", title: "8 hour shifts", shifts: "M / A / N" },
	{ kind: "12h", title: "12 hour shifts", shifts: "D / N" },
];

// Sources come plural ("Weld tests", "Logbook entries")
const singular = (s: string) =>
	s.endsWith("ies")
		? `${s.slice(0, -3)}y`
		: s.endsWith("s")
			? s.slice(0, -1)
			: s;

const describeImpact = (rows: LineScheduleDayImpact[]) =>
	rows
		.map(
			({ Source, Cnt }) =>
				`${Cnt} ${(Cnt === 1 ? singular(Source) : Source).toLowerCase()}`,
		)
		.join(", ");

type Props = {
	assetId: number;
	date: Date;
	currentKind: DayKind | null;
	lineDefaultKind: DayKind | null;
	saving?: boolean;
	onSave: (kind: DayKind | null, spells: ShiftSpellChange[]) => void;
	onClose: () => void;
};

export const DayConfigModal = ({
	assetId,
	date,
	currentKind,
	lineDefaultKind,
	saving = false,
	onSave,
	onClose,
}: Props) => {
	const dateKey = toDateKey(date);
	const [selected, setSelected] = useState(currentKind);
	const [pending, setPending] = useState(new Map<number, boolean>());
	const [impactError, setImpactError] = useState("");
	const shiftsPerDay = selected == null ? null : shiftsPerDayOf(selected);
	const {
		data: options,
		isLoading,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: ["dayShiftOptions", assetId, dateKey, shiftsPerDay],
		queryFn: () =>
			getDayShiftOptions({ assetId, productionDate: dateKey, shiftsPerDay }),
		enabled: shiftsPerDay != null,
		meta: { realtime: ["FAB.GetDayShiftOptions"] },
	});

	// Once per open: what a shape change re-stamps does not depend on the kind
	const { data: impact, isLoading: impactLoading } = useQuery({
		queryKey: ["dayConfigImpact", assetId, dateKey],
		queryFn: ({ signal }) => getLineScheduleDayImpact(assetId, dateKey, signal),
		refetchOnWindowFocus: false,
		onError: (e: Error) => setImpactError(e.message),
	});
	const impactTotal = impact?.reduce((n, r) => n + r.Cnt, 0) ?? 0;

	const selectKind = (kind: DayKind | null) => {
		if (kind == null || kind === selected) return;
		setSelected(kind);
		setPending(new Map());
	};
	const toggleSpell = (shiftId: number, isSpell: boolean) =>
		setPending((prev) => {
			const next = new Map(prev);
			const shift = options?.find((s) => s.ShiftId === shiftId);
			const matchesSaved =
				shift &&
				(isSpell
					? shift.SpellCount === shift.BreakCount
					: shift.SpellCount === 0);
			if (matchesSaved) next.delete(shiftId);
			else next.set(shiftId, isSpell);
			return next;
		});

	const kindChanged = selected != null && selected !== currentKind;
	const canSave =
		!saving &&
		!isLoading &&
		!isError &&
		!!options?.length &&
		selected != null &&
		(kindChanged || pending.size > 0);

	return (
		<MesfModal
			open
			handleClose={() => !saving && onClose()}
			title={`Day Config — ${moment(dateKey, "YYYY-MM-DD").format("ddd MM/DD/YYYY")}`}
			maxWidth="sm"
		>
			<MesfModal.Content dividers>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
					Shift scheme for this day on this line
				</Typography>
				<ToggleButtonGroup
					exclusive
					fullWidth
					color="primary"
					value={selected}
					disabled={saving}
					onChange={(_, value: DayKind | null) => selectKind(value)}
				>
					{OPTIONS.map((opt) => (
						<ToggleButton
							key={opt.kind}
							value={opt.kind}
							sx={{
								textTransform: "none",
								py: 1.5,
								flexDirection: "column",
								gap: 0.25,
								"&.Mui-selected": {
									backgroundColor: "primary.main",
									color: "primary.contrastText",
									"&:hover": { backgroundColor: "primary.dark" },
								},
							}}
						>
							<Typography variant="body2" fontWeight={700}>
								{opt.title}
							</Typography>
							<Typography variant="caption">{opt.shifts}</Typography>
							{opt.kind === lineDefaultKind && (
								<Typography
									variant="caption"
									sx={{ fontStyle: "italic", opacity: 0.75 }}
								>
									line default
								</Typography>
							)}
						</ToggleButton>
					))}
				</ToggleButtonGroup>

				{kindChanged &&
					(impactLoading ? (
						<Skeleton variant="rounded" height={56} sx={{ mt: 2 }} />
					) : (
						impact && (
							<Alert severity="info" sx={{ mt: 2 }}>
								{impact.length ? (
									<>
										<Typography variant="body2">
											Saving moves {impactTotal}{" "}
											{impactTotal === 1 ? "record" : "records"} to the new
											shifts: {describeImpact(impact)}.
										</Typography>
										<Typography variant="body2">
											Breaks within this production day are regenerated from the
											rules. Breaks moved outside it stay where you put them.
											Undo restores the day's previous breaks.
										</Typography>
									</>
								) : (
									<Typography variant="body2">
										No records on this day yet. Breaks are regenerated from the
										rules. Breaks moved outside this production day stay where
										you put them.
									</Typography>
								)}
							</Alert>
						)
					))}

				{isError ? (
					<Alert
						severity="error"
						sx={{ mt: 2 }}
						action={
							<Button color="inherit" onClick={() => refetch()}>
								Retry
							</Button>
						}
					>
						{error instanceof Error ? error.message : "Unable to load shifts"}
					</Alert>
				) : (
					<ShiftSpellToggles
						options={options}
						loading={isLoading}
						pending={pending}
						disabled={saving}
						onToggle={toggleSpell}
					/>
				)}
			</MesfModal.Content>
			<MesfModal.Actions sx={{ px: 3, py: 2 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<Button variant="outlined" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={() =>
							onSave(
								kindChanged ? selected : null,
								[...pending].map(([shiftId, isSpell]) => ({
									shiftId,
									isSpell,
								})),
							)
						}
						disabled={!canSave}
						startIcon={
							saving ? <CircularProgress size={16} color="inherit" /> : null
						}
					>
						Save
					</Button>
				</Box>
			</MesfModal.Actions>
			<ErrorModal error={impactError} onHide={() => setImpactError("")} />
		</MesfModal>
	);
};
